/**
 * 加载数据
 */

import { SetStateAction, useCallback, useRef, useState } from 'react';

import { ApiResponse } from '@/services';

interface FetchDataOptions<T> {
  fetchFn: (pageNum: number, pageSize: number) => Promise<ApiResponse<T>>;
  pageSize?: number;
}

interface FetchDataResult<T> {
  data: T[];
  loading: boolean;
  error: Error | null;
  pageSize: number;
  pageNum: number;
  total: number;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
  updateData: (value: SetStateAction<T[]>) => void;
  clearData: () => void;
  rawResponse: ApiResponse<T> | null;
}

function isNoData(response: { data?: any; items?: any }) {
  if (!response.data && !response.items) {
    return true;
  }
  if (response.data?.length || response.items?.length) {
    return false;
  }
  return true;
}

function useFetchData<T>({
  fetchFn,
  pageSize = 10,
}: FetchDataOptions<T>): FetchDataResult<T> {
  const [data, setData] = useState<T[]>([]);
  const [rawResponse, setRawResponse] = useState<ApiResponse<T> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const pageNum = useRef(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const recentHasData = useRef(false);
  const loadingRef = useRef(false);

  const loadData = useCallback(
    async (currentPageNum: number, replace = false) => {
      if (loadingRef.current) return;
      loadingRef.current = true;
      setLoading(true);
      setError(null);

      try {
        const response = await fetchFn(currentPageNum, pageSize);
        setRawResponse(response);
        if (response.code === 200 && response.success) {
          const newData = (response.items || response.data || []) as T[];
          setData((prev) => (replace ? newData : [...prev, ...newData]));
        }

        recentHasData.current = !isNoData(response);
        setTotal(response.totalCount);
        setHasMore(!isNoData(response));
      } catch (err) {
        setError(err instanceof Error ? err : new Error('加载失败'));
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    },
    [fetchFn, pageSize],
  );

  const loadMore = useCallback(async () => {
    if (loadingRef.current || !recentHasData.current) return;
    pageNum.current += 1;
    await loadData(pageNum.current);
  }, [loadData]);

  const refresh = useCallback(async () => {
    pageNum.current = 1;
    await loadData(1, true);
  }, [loadData]);

  const updateData = useCallback((value: SetStateAction<T[]>) => {
    setData(value);
  }, []);

  const clearData = useCallback(() => {
    setData([]);
  }, []);

  return {
    data,
    loading,
    error,
    pageNum: pageNum.current,
    pageSize,
    total,
    hasMore,
    loadMore,
    refresh,
    updateData,
    clearData,
    rawResponse,
  };
}

export default useFetchData;
